import { toPng } from 'html-to-image';
import { preloadImagesForCapture } from '@/lib/preloadImagesForCapture';
import { periodLabel, sortByPoints } from '../gameUtils';

// Render a capture node to a 2x PNG at a fixed desktop-scale width so the
// export fills the canvas on every device (portrait 900px, landscape 1600px).
export async function captureBoxScore(node, variant) {
  if (!node) return null;
  const targetWidth = variant === 'landscape' ? 1600 : 900;
  node.classList.add('box-export-mode');
  const prevInlineWidth = node.style.width;
  const prevInlineMinWidth = node.style.minWidth;
  node.style.width = `${targetWidth}px`;
  node.style.minWidth = `${targetWidth}px`;
  // eslint-disable-next-line no-unused-expressions
  node.offsetHeight;
  node.scrollTop = 0;
  // Inline logos as data URLs so mobile browsers don't drop them.
  const restoreImages = await preloadImagesForCapture(node);
  const fullHeight = Math.max(node.scrollHeight, node.offsetHeight);
  try {
    return await toPng(node, {
      backgroundColor: '#0A0F2C',
      cacheBust: true,
      pixelRatio: 2,
      width: targetWidth,
      height: fullHeight,
      canvasWidth: targetWidth,
      canvasHeight: fullHeight,
      style: { transform: 'none', maxHeight: 'none', overflow: 'visible' },
    });
  } finally {
    restoreImages();
    node.classList.remove('box-export-mode');
    node.style.width = prevInlineWidth;
    node.style.minWidth = prevInlineMinWidth;
  }
}

export const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

export const downloadDataUrl = (dataUrl, name) => {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = name;
  a.click();
};

export const boxScoreFileName = (game, quarter) =>
  `${game.team_a.name}-vs-${game.team_b.name}-${periodLabel(quarter)}.png`
    .replace(/\s+/g, '-').replace(/[^a-zA-Z0-9.-]/g, '');

const playerLine = (p) => {
  const pm = p.plus_minus ?? 0;
  const pmStr = pm === 0 ? '' : (pm > 0 ? ` +${pm}` : ` ${pm}`);
  const stat = `${p.pts}p ${p.reb}r ${p.ast}a` +
    (p.stl ? ` ${p.stl}s` : '') +
    (p.blk ? ` ${p.blk}b` : '') +
    (p.to ? ` ${p.to}to` : '') +
    pmStr;
  return `#${p.jersey || '?'} ${p.name}  —  ${stat}`;
};

// Plain-text box score for WhatsApp / Mail intent links.
export function buildShareText({ game, boxscore, teamTotals, quarter, scoreA, scoreB }) {
  const lines = [
    `${game.team_a.name} ${scoreA} — ${game.team_b.name} ${scoreB} (${periodLabel(quarter)})`,
    '',
  ];
  for (const tk of ['team_a', 'team_b']) {
    lines.push(game[tk].name.toUpperCase());
    for (const p of sortByPoints(boxscore[tk])) {
      if ((p.pts + p.reb + p.ast + p.stl + p.blk + p.to + p.fga + p.fta) === 0 && (p.plus_minus ?? 0) === 0) continue;
      lines.push(playerLine(p));
    }
    const t = teamTotals[tk];
    lines.push(`Team: ${t.pts}p ${t.reb}r (${t.oreb}or/${t.dreb}dr) ${t.ast}a · FG ${t.fgm}/${t.fga} (${t.fg_pct}%) · 3P ${t.three_m}/${t.three_a} · FT ${t.ftm}/${t.fta}`);
    lines.push('');
  }
  lines.push('via Schedule Guru');
  return lines.join('\n');
}
